/* eslint max-lines: 0 */
import { useState } from 'react';
import { Flex } from '@chakra-ui/react';
import omit from 'lodash/omit';
import { AudioTitle } from 'app/components/Song/Audio/AudioTitle';
import { format } from 'date-fns';
import { dateFormats } from 'app/constants';
import { AudioSelectionCheckbox } from 'app/components/Releases/AudioSelectionCheckbox';
import { ListItemText, TableList } from 'app/components/ChakraTableList';
import { AudioPlayControls } from 'app/components/Song/Audio/AudioPlayControls';
import { ReactComponent as AudioIcon } from 'resources/images/audio.svg';
import { ChakraEmptyList } from 'app/components/Core';
import { CheckboxButton } from 'app/components/Core/CheckboxButton/CheckboxButton';
import {
  AudioFilterBar,
  AudioSearchBar,
  AudioSelectActions,
} from 'app/components/Audio';
import { useLabelV2FeatureFlag } from 'featureFlags';
import {
  addAudioToChecked,
  AUDIO_SELECTION_LIST_CONTAINER_ID,
  AUDIO_SELECTION_LIST_ID,
  audioSelectListColumns,
  getAudioTypesFilters,
  getListLoadingOptions,
  getRowState,
} from './utils';
import { AudioSelectionItem, AudioSelectListProps } from './types';

export const AudioSelectListComponent = ({
  fetchMore,
  hasMore,
  loading,
  loadingMore,
  audioList,
  maxAudioCount,
  setChecked,
  checked,
  audioTypes,
  onMaxAudioFilesSelected,
  search,
  onCancel,
  onAdd,
  onSearch,
  isSubmitting,
  emptyStateMessage,
  songAudioListValidation,
  disabledAudios = {
    audios: [],
    reason: '',
  },
  onInvalidAudioSelect,
  filters,
  onFilter,
}: AudioSelectListProps) => {
  const { labelV2ReleaseEnabled } = useLabelV2FeatureFlag();
  const [allChecked, setAllChecked] = useState(filters.types.length === 0);
  const [latestVersions, setLatestVersions] = useState(
    filters.onlyLatestVersionAudio
  );

  const checkedFilesCount = Object.keys(checked).length;
  const showEmptyState = !loading && audioList.length === 0;
  const shouldHideSearch =
    showEmptyState && search === '' && !filters.types.length;
  const shouldDisableSubmit =
    ((showEmptyState || loading) && checkedFilesCount === 0) || isSubmitting;
  const shouldHideToggleFilter = !audioTypes;

  const onFilterChange = (value: (string | number)[]) => {
    setAllChecked(value.length === 0 && !latestVersions);
    onFilter({ types: value as string[] });
  };

  return (
    <>
      {!shouldHideSearch && (
        <AudioSearchBar
          onSearch={onSearch}
          songAudioListValidation={songAudioListValidation}
        />
      )}
      {!shouldHideToggleFilter && (
        <AudioFilterBar
          justifyContent="center"
          flexWrap="wrap"
          pb={12}
          maxWidth={586}
          buttonGroupOptions={getAudioTypesFilters(audioTypes)}
          defaultValues={filters.types}
          onFilterChange={onFilterChange}
        >
          <>
            <CheckboxButton
              buttonTitle="All"
              isChecked={allChecked}
              onChange={(e) => {
                setAllChecked(e.currentTarget.checked);
                onFilter({ types: [], onlyLatestVersionAudio: false });
                setLatestVersions(false);
              }}
            />
            <CheckboxButton
              buttonTitle="Latest versions"
              isChecked={latestVersions}
              onChange={(e) => {
                setLatestVersions(e.currentTarget.checked);
                const onlyLatestVersionAudio = e.currentTarget.checked;
                const hasTypeFilters = filters.types.length > 0;
                const nofilters = !onlyLatestVersionAudio && !hasTypeFilters;

                setAllChecked(nofilters);
                onFilter({
                  onlyLatestVersionAudio,
                  ...(nofilters ? { types: [] } : {}),
                });
              }}
            />
          </>
        </AudioFilterBar>
      )}
      {showEmptyState ? (
        <ChakraEmptyList
          icon={<AudioIcon role="img" />}
          title="No Audio Files"
          primaryText={emptyStateMessage}
          overrideText={search ? `No results found for "${search}"` : ''}
        />
      ) : (
        <Flex
          direction="column"
          height="100%"
          width="100%"
          overflowY="auto"
          id={AUDIO_SELECTION_LIST_CONTAINER_ID}
        >
          <TableList
            id={AUDIO_SELECTION_LIST_ID}
            scrollable
            fetchMore={fetchMore}
            hasMore={hasMore}
            wrapperSx={{
              overflow: 'hidden',
            }}
            loadingOptions={getListLoadingOptions(loading, loadingMore)}
            columns={audioSelectListColumns}
            onRowClick={(row: { rowData: { audio: AudioSelectionItem } }) => {
              const { audio } = row.rowData;

              if (disabledAudios.audios.includes(audio.guid)) {
                return;
              }

              if (audio.validationResult && !audio.validationResult.isValid) {
                const { errorCode } = audio.validationResult.errors[0];
                if (onInvalidAudioSelect) {
                  onInvalidAudioSelect(errorCode);
                }
                return;
              }

              const { isChecked, disableNext } = getRowState({
                checked,
                audio,
                maxAudioCount,
              });

              if (disableNext && !isChecked) {
                onMaxAudioFilesSelected();
                return;
              }

              if (isChecked) {
                setChecked(omit(checked, [audio.guid]));
              } else {
                setChecked(
                  addAudioToChecked(checked, audio, labelV2ReleaseEnabled)
                );
              }
            }}
            rows={audioList?.map((item, i) => {
              const {
                isChecked,
                disableNext,
                isError,
                isLoading,
                isValid,
              } = getRowState({
                checked,
                audio: item,
                maxAudioCount,
              });

              const disabledTooltip = disabledAudios.audios.includes(item.guid)
                ? disabledAudios?.reason
                : '';

              return {
                clickable: !disabledTooltip,
                tooltip: disabledTooltip,
                disabled: Boolean(disabledTooltip) || !isValid,
                rowData: {
                  isError,
                  disabled: disableNext,
                  audio: item,
                  isValid,
                },
                controls: (
                  <AudioPlayControls
                    isLoading={isLoading}
                    audioGuid={item.guid}
                    parentGuid={item.parent}
                    isError={isError}
                    songDetails={item.songDetails}
                  />
                ),
                title: (
                  <AudioTitle
                    audioGuid={item.guid}
                    title={item.title}
                    isLatestVersion={item.isLatestVersion}
                  />
                ),
                song: (
                  <ListItemText
                    primary={item.songDetails.title}
                    title={item.songDetails.title}
                    data-testid="audio-selection-song-title"
                  />
                ),
                date: (
                  <ListItemText
                    data-testid="audio-selection-date"
                    primary={format(
                      item.created.timestamp * 1000,
                      dateFormats.LIST_FORMAT
                    )}
                  />
                ),
                audioType: (
                  <ListItemText
                    primary={audioTypes[item.type]?.name}
                    data-testid="audio-selection-type"
                  />
                ),
                checkbox: (
                  <AudioSelectionCheckbox
                    index={i}
                    key={item.guid}
                    disableNext={
                      disableNext || Boolean(disabledTooltip) || !isValid
                    }
                    isChecked={isChecked}
                  />
                ),
              };
            })}
          />
        </Flex>
      )}
      <AudioSelectActions
        disabled={shouldDisableSubmit}
        checkedItemsCount={checkedFilesCount}
        pluralCountSuffix="files"
        singularCountSuffix="file"
        onCancel={onCancel}
        onClick={onAdd}
      />
    </>
  );
};
